'use client'

import React, { useEffect, useState } from 'react'
import { Box } from '@mantine/core'

interface Props extends React.ComponentPropsWithoutRef<typeof Box> {
    /** Fully-qualified URL to the SVG that will be used as the CSS mask */
    src: string
    /** Square dimension (px / css unit). Defaults to `150` px. */
    size?: number | string
    /** Extra inline styles to merge with the gradient mask styles */
    style?: React.CSSProperties
}

/**
 * Renders an SVG as a mask that is filled by our brand gradient.
 *
 * The SVG never appears in the DOM by itself – it is referenced through
 * `mask-image` (and the web-kit prefixed variant) which lets us colour any
 * monochrome SVG shape without having to edit the file.
 *
 * Because the component is completely self-contained it can be dropped
 * anywhere in the codebase.
 */
export const GradientSvgIcon: React.FC<Props> = ({ src, size = 150, style, ...rest }) => {
    const [maskSrc, setMaskSrc] = useState(src) // ← local url

    /* ------------------------------------------------
       Convert remote SVG to same-origin blob: URL
    -------------------------------------------------*/
    useEffect(() => {
        let blobUrl: string | undefined

        // Always fetch → convert → blob: URL
        fetch(src)
            .then((r) => r.blob())
            .then((b) => {
                blobUrl = URL.createObjectURL(b)
                setMaskSrc(blobUrl)
            })
            .catch((err) => {
                console.error('GradientSvgIcon fetch failed:', err)
                setMaskSrc(src) // fall back to original URL
            })

        return () => {
            if (blobUrl) URL.revokeObjectURL(blobUrl)
        }
    }, [src])

    const dim = typeof size === 'number' ? `${size}px` : size

    return (
        <Box
            {...rest}
            style={{
                width: dim,
                height: dim,
                background: 'linear-gradient(0deg, #A69A91 0%, #BFB1A7 100%)',
                /* ---- mask ---- */
                maskImage: `url("${maskSrc}")`,
                WebkitMaskImage: `url("${maskSrc}")`,
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskPosition: 'center',
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                display: 'inline-block',
                ...style,
            }}
        />
    )
}

export default GradientSvgIcon
